import {onMounted, onUnmounted, ref} from "vue";
import { getCSSVars } from '@/config/theme.js';
import { branding } from '@/config/branding.js';

const LS_COLOR_SCHEME_KEY = 'launchpad:scheme';

/**
 * Applies a light/dark color scheme to the document: sets the `scheme` and
 * `data-theme` attributes, toggles the `dark` class, swaps CSS custom
 * properties from the current brand's theme preset, and swaps the favicon
 * between its light/dark variants.
 *
 * @param {'light'|'dark'} scheme - The color scheme to apply
 * @param {boolean} [persist=false] - Whether to save the scheme to localStorage so it's remembered on reload
 */
function applyScheme(scheme, persist = false) {
  document.documentElement.setAttribute('scheme', scheme);
  document.documentElement.setAttribute('data-theme', branding.themePreset);
  if (scheme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
  if (persist) {
    localStorage.setItem(LS_COLOR_SCHEME_KEY, scheme);
  }

  const vars = getCSSVars(branding.themePreset, scheme);
  Object.entries(vars).forEach(([key, value]) => {
    document.documentElement.style.setProperty(key, value);
  });

  const favicon = /** @type {HTMLLinkElement | null} */ (document.getElementById('brand-favicon'));
  if (favicon) {
    const lightIcon = favicon.getAttribute('data-light');
    const darkIcon = favicon.getAttribute('data-dark');
    if (lightIcon && darkIcon) {
      favicon.href = scheme === 'dark' ? darkIcon : lightIcon;
    }
  }
}

/**
 * Reads the OS/browser-level color scheme preference.
 * @returns {'light'|'dark'} 'dark' if the system prefers a dark color scheme, otherwise 'light'
 */
function getSystemScheme() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Reads the user's explicitly saved scheme preference from localStorage.
 * @returns {'light'|'dark'|null} the saved scheme, or null if none was saved
 */
function getSavedScheme() {
  return /** @type {'light'|'dark'|null} */ (localStorage.getItem(LS_COLOR_SCHEME_KEY));
}

/**
 * Clears the saved scheme preference from localStorage, reverting future
 * scheme resolution to the system preference.
 */
function removeSavedScheme() {
  localStorage.removeItem(LS_COLOR_SCHEME_KEY);
}

/**
 * Sets up a listener that re-applies the system color scheme whenever it
 * changes, but only while the user has no explicitly saved preference.
 * Also applies the scheme once immediately on setup.
 *
 * @returns {Function} a cleanup function that removes the listener
 */
function initSystemSchemeListener() {
  const matchMedia = window.matchMedia('(prefers-color-scheme: dark)');
  const updateScheme = () => {
    const savedScheme = getSavedScheme();
    if (!savedScheme) {
      applyScheme(getSystemScheme());
    }
  };

  matchMedia.addEventListener('change', updateScheme);
  updateScheme();

  return () => {
    matchMedia.removeEventListener('change', updateScheme);
  };
}

/**
 * Vue composable that tracks the document's current `scheme` attribute as
 * reactive state, updating it via a MutationObserver whenever the attribute
 * changes elsewhere (e.g. from `applyScheme`). Starts observing on mount and
 * disconnects on unmount.
 *
 * @returns {import('vue').Ref<string>} reactive ref holding the current scheme ('light'/'dark')
 */
function useSchemeObserver() {
  const currentScheme = ref(document.documentElement.getAttribute('scheme') || 'dark');

  let observer;

  const startObserver = () => {
    observer = new MutationObserver(() => {
      currentScheme.value = document.documentElement.getAttribute('scheme') || 'dark';
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['scheme'],
    });
  };

  const stopObserver = () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  };

  onMounted(startObserver);
  onUnmounted(stopObserver);

  return currentScheme;
}

export { applyScheme, getSystemScheme, getSavedScheme, removeSavedScheme, initSystemSchemeListener, useSchemeObserver };
